import React, { useEffect, useMemo } from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { errors } from 'components/BaseComponents/constants'

import FHeading1 from 'components/BaseComponents/Text/FHeading1'
import FParagraphGroup from 'components/BaseComponents/Text/FParagraphGroup'

import FInput from 'components/BaseComponents/FInput'
import FButton from 'components/BaseComponents/FButton'
import SBox from 'components/opinionless/SBox'
import { useSTheme } from 'styles/theme'
import { useTranslation } from 'i18n'
import FNeedHelpCTA from 'components/BaseComponents/FNeedHelpCTA'
import { StepWizardChildProps } from 'react-step-wizard'
import { useBenefitsAnswerQueryQuery, useBenefitsAnswersMutation } from '../../../../../types/graphql'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { ga4_useTitle } from 'utils/ga4'
import { LBTextHeader, LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'

interface Props {
    onFinish: () => void
    onPrevious: () => void
    personId: any
}

const HUDHousingIntro: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const form = useForm()
    const theme = useSTheme()
    const { t } = useTranslation()

    const paragraphs = useMemo(() => {
        return [t('hud_housing_paragraph_1')]
    }, [t])

    const [saveBenefitAnswer, { loading }] = useBenefitsAnswersMutation()
    const { data, loading: queryLoading } = useBenefitsAnswerQueryQuery({
        variables: { personId: props.personId },
        fetchPolicy: 'network-only',
    })

    ga4_useTitle('HUD housing')

    const onSubmit = async (values) => {
        await saveBenefitAnswer({
            variables: {
                answers: [
                    {
                        personId: props.personId,
                        category: 'HOUSING_ASSISTANCE',
                        answer: values.hud_housing,
                    },
                ],
            },
        })
        props.onFinish()
    }

    useEffect(() => {
        if (data) {
            const savedValue = data.benefitsAnswers.find((answer: any) => answer.category === 'HOUSING_ASSISTANCE')
                ?.answer
            form.setValue('hud_housing', savedValue)
        }
    }, [data])

    return (
        <FormProvider {...form}>
            <SBox as={'form'} onSubmit={form.handleSubmit(onSubmit)}>
                <FButtonPrevious onClick={props.onPrevious} />
                <LBTextHeader
                    sx={{
                        textAlign: 'left',
                        fontSize: '32px',
                        color: theme.colors.secondaryColor,
                        lineHeight: '120%',
                        fontFamily: 'Raleway',
                        fontWeight: '900',
                    }}
                >
                    {t('hud_housing_header')}
                </LBTextHeader>
                <LBTextSubHeader
                    color={theme.colors.blackLabelfont}
                    dangerouslySetInnerHTML={{
                        __html: t('hud_housing_paragraph_1'),
                    }}
                />
                <FInput
                    color={theme.colors.blackLabelfont}
                    label={t('hud_housing_amount_label')}
                    labelColor={theme.colors.blackLabelFontAlpha}
                    id={'hud_housing'}
                    name={'hud_housing'}
                    type={'number'}
                    ref={form.register({
                        required: errors.default_required_key as string,
                        validate: {
                            positiveNumber: (value) => parseInt(value) >= 0 || 'positive_integer_validation',
                        },
                    })}
                    placeholder={t('hud_housing_amount_placeholder')}
                />
                <FButton
                    buttonType={'filled'}
                    type={'submit'}
                    loading={loading}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('save_and_continue')}
                </FButton>
            </SBox>
        </FormProvider>
    )
}

export default HUDHousingIntro
